import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ORGANIZATIONS_LOGO_URL } from 'shared/constants';
import { attendanceKeys, useCheckInSession } from 'shared/hooks/attendance.hooks';
import { useCheckInBoundaryRefetch } from 'shared/hooks/check-in-boundary.hooks';
import { scheduleEventKeys, useScheduleEventDetail } from 'shared/hooks/schedule-events.hooks';
import { Loader } from 'src/components/core';
import monitorService from 'src/helpers/monitor.service';
import { LogoWhite } from 'src/images';
import { QrCheckInCard } from './components/check-in/qr-check-in-card';
import { CodeCountdown } from './components/check-in/code-countdown';
import { DownloadQrButton } from './components/check-in/download-qr-button';
import { getCheckInSchedule, isStaticQr } from './helpers/event-view.helpers';
import { useEducationalEventFormat } from './helpers/use-educational-event-format';
import styles from './check-in-fullscreen.module.scss';

export function CheckInFullscreenPage() {
  const { id } = useParams();
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const orgName = useSelector(({ organization }) => organization?.name);
  const orgLogo = useSelector(({ organization }) => organization?.logo);
  const queryClient = useQueryClient();
  const { formatTimeOrDateTime } = useEducationalEventFormat();

  const eventQuery = useScheduleEventDetail(id);
  const event = eventQuery.data ?? null;
  const { session, loaded: sessionLoaded, error: sessionError } = useCheckInSession({ id });

  const isOpen = !!session;
  const { opensAt, closesAt } = getCheckInSchedule(event);
  useCheckInBoundaryRefetch({
    opensAt,
    closesAt,
    isOpen,
    onBoundary: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: scheduleEventKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: attendanceKeys.session(id) }),
      ]),
  });

  const loadError = eventQuery.error || sessionError;
  const loading = !loadError && (eventQuery.isPending || !sessionLoaded);
  const error = !!loadError;

  useEffect(() => {
    if (loadError) {
      monitorService.error('Failed to load fullscreen check-in', { error: loadError, eventId: id });
    }
  }, [loadError, id]);

  const isStatic = isStaticQr(event);

  const renderCard = () => {
    if (error) return <p className={styles.closedText}>{lang.CHECK_IN_LOAD_FAILED}</p>;
    if (!isOpen) {
      return (
        <p className={styles.closedText}>
          {opensAt
            ? lang.CHECK_IN_OPENS_AT.replace('{time}', formatTimeOrDateTime(opensAt))
            : lang.CHECK_IN_NOT_OPEN}
        </p>
      );
    }
    return (
      <>
        <h2 className={styles.cardTitle}>
          {lang.CHECK_IN_TO_EVENT.replace('{title}', event?.title || '')}
        </h2>
        <span className={styles.cardSubtitle}>{lang.SCAN_TO_CHECK_IN}</span>
        <QrCheckInCard
          eventId={id}
          isStatic={isStatic}
          totpSecret={session?.totpSecret}
          step={session?.step}
        />
        {!isStatic && session?.step ? (
          <CodeCountdown step={session.step} className={styles.countdown} />
        ) : null}
      </>
    );
  };

  return (
    <div className={styles.screen}>
      <div className={styles.brand}>
        {orgLogo ? (
          <img
            src={`${ORGANIZATIONS_LOGO_URL}web/${orgLogo}`}
            alt={orgName || ''}
            className={styles.brandLogo}
          />
        ) : (
          <LogoWhite className={styles.brandLogo} />
        )}
      </div>
      <div className={styles.center}>
        {loading ? (
          <Loader />
        ) : (
          <div className={styles.card}>
            {renderCard()}
            {isStatic && !error && (
              <DownloadQrButton
                eventId={id}
                eventTitle={event?.title}
                className={styles.downloadButton}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
